import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_VOLUME_KEY, ManagedVolume, storageAPI } from "../StorageAPI";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

/**
 * Load the customer's managed-file volumes and track which one is being browsed.
 *
 * A customer with only the platform volume gets a single-entry list, which every consumer renders as
 * nothing at all — so a tenant that never configured an extra SharePoint sees the UI exactly as
 * before. The active key starts at `"default"`, the alias the server resolves to the configured
 * default volume, so the first listing request is correct before the volume list has even arrived.
 */
export function useVolumes(customerID?: string) {
    const [volumes, setVolumes] = useState<ManagedVolume[]>([]);
    const [activeVolumeKey, setActiveVolumeKey] = useState<string>(DEFAULT_VOLUME_KEY);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Bumped by `reload()`. The settings panel edits the volume list, so it needs to re-read what
    // the server now derives; the read-only pickers never call it.
    const [refreshIndex, setRefreshIndex] = useState<number>(0);

    useEffect(() => {
        let cancelled = false;
        if (!customerID) {
            setVolumes([]);
            return;
        }
        setIsLoading(true);
        storageAPI
            .getVolumes(customerID)
            .then(loaded => {
                if (cancelled) return;
                setVolumes(loaded);
                // Pin the alias to a concrete key once we know it, so the selector can show which
                // volume is actually being browsed.
                const fallback = loaded.find(volume => volume.isDefault) ?? loaded[0];
                setActiveVolumeKey(current =>
                    current === DEFAULT_VOLUME_KEY && fallback ? fallback.key : current
                );
            })
            .catch(() => {
                // A customer whose volume list cannot be loaded keeps working against "default".
                if (!cancelled) setVolumes([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [customerID, refreshIndex]);

    const activeVolume = useMemo(
        () => volumes.find(volume => volume.key === activeVolumeKey),
        [volumes, activeVolumeKey]
    );

    const selectVolume = useCallback((key: string) => setActiveVolumeKey(key), []);

    const reload = useCallback(() => setRefreshIndex(index => index + 1), []);

    return { volumes, activeVolumeKey, activeVolume, selectVolume, isLoading, reload };
}

interface VolumeSelectorProps {
    volumes: ManagedVolume[];
    activeVolumeKey: string;
    onSelect: (key: string) => void;
    disabled?: boolean;
    label?: string;
}

/**
 * Volume switcher, for **read-only** views only — the file pickers, where browsing another volume is
 * the point. Write surfaces (the file manager) address the internal S3 volume explicitly instead;
 * see `PLATFORM_VOLUME_KEY`.
 *
 * Renders nothing when the customer has at most one volume — the overwhelmingly common case, and
 * showing a dropdown with a single immutable entry would be pure noise.
 */
export default function VolumeSelector({
    volumes,
    activeVolumeKey,
    onSelect,
    disabled = false,
    label,
}: VolumeSelectorProps) {
    const { t } = useTranslation();
    const selectId = useId();

    if (volumes.length < 2) {
        return null;
    }

    const options: { key: string; text: string }[] = volumes.map(volume => ({
        key: volume.key,
        // The platform volume's stored name is its key, which is not a label — translate that one.
        text: volume.isPlatform && volume.name === volume.key
            ? t('storage.volumes.platform', 'Standard-Ablage')
            : volume.name,
    }));

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={selectId}>{label ?? t('storage.volumes.label', 'Ablage')}</Label>
            <Select
                value={activeVolumeKey}
                disabled={disabled}
                onValueChange={(value) => {
                    if (value) onSelect(String(value));
                }}
            >
                <SelectTrigger id={selectId} className="min-w-0">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {options.map(option => (
                        <SelectItem key={option.key} value={option.key}>{option.text}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
